<!-- 
  TANKS LTAP INSPECTION FIELDS COMPONENT
  ======================================
  This component handles inspection data input for LTAP (Local Metal Loss Profile) 
  when in MFH (Tank) mode (strD_iclass == 2).
  
  All fields use clean, descriptive IDs prefixed with "ltap_" for clarity.
  The (change) events emit to parent via @Output() EventEmitters.
-->
<ng-container [formGroup]="formGroup">

  <!-- Radio buttons for LTA/Groove are rendered by the parent in the "Type of Metal Loss" fieldset -->

  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <!-- FIELD 1: Any other thinned areas infringe on the 2s x 2c box?               -->
  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <div class="form-group row align-items-center">
    <div class="col-sm-8 align-self-center">
      <label for="ltap_2s2cBox">Any other thinned areas infringe on the 2s X 2c box?</label>
    </div>
    <div class="col-sm-4">
      <select
        id="ltap_2s2cBox"
        class="form-control form-control-sm"
        formControlName="ltap_2s2cBox"
        (change)="onComboBox1Change($any($event.target).value)"
      >
        <option value="Initial" disabled selected hidden></option>
        <option value="Yes">Yes</option>
        <option value="No">No</option>
      </select>
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <!-- FIELD 2: Metal Loss Data Spacing in Axial Direction (sp) - spacing between adjacent measurements -->
  <!-- Note: Enter 0 to import thickness profile data from CSV                     -->
  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <div class="form-group row align-items-center">
    <div class="col-sm-8 align-self-center">
      <label for="ltap_metalLossLength_s">
        Metal loss data spacing in the axial direction (0 to import data from CSV), sp&nbsp;
        <span *ngIf="intUnit === 0">(inch)</span>
        <span *ngIf="intUnit === 1">(mm)</span>:
      </label>
    </div>
    <div class="col-sm-4">
      <input
        appRestrictInput
        type="text"
        id="ltap_metalLossLength_s"
        name="ltap_metalLossLength_s"
        class="form-control form-control-sm"
        formControlName="ltap_metalLossLength_s"
        [value]="lmlProfileData_sngGrid_spacing"
        (change)="onSChange($event)"
        (input)="onInput_lmlProfileData_sngGrid_spacing($event)"
        autocomplete="off"
      />
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <!-- FIELD 3: Number of Thickness Measurements (longitudinal direction)          -->
  <!-- This determines the grid size for the thickness profile table               -->
  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <div class="form-group row align-items-center">
    <div class="col-sm-8 align-self-center">
      <label for="ltap_numMeasurements">Total number of thickness readings in the longitudinal direction including 
        the bounding readings in sound metal adjacent to the LML region, n:</label>
    </div>
    <div class="col-sm-4">
      <input
        appRestrictInput
        type="number"
        id="ltap_numMeasurements"
        name="ltap_numMeasurements"
        class="form-control form-control-sm"
        formControlName="ltap_numMeasurements"
        [value]="lmlProfileData_intnum_t_pt"
        (change)="onNumMeasurementsChange($event)"
        (input)="onInput_lmlProfileData_intnum_t_pt($event)"
        min="0"
      />
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <!-- FIELD 4: Future Corrosion Allowance (FCAml)                                 -->
  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <div class="form-group row align-items-center">
    <div class="col-sm-8 align-self-center">
      <label for="ltap_fcaml">
        Future corrosion allowance inside the damage area, FCAml&nbsp;
        <span *ngIf="intUnit === 0">(inch)</span>
        <span *ngIf="intUnit === 1">(mm)</span>:
      </label>
    </div>
    <div class="col-sm-4">
      <input
        appRestrictInput
        type="number"
        id="ltap_fcaml"
        name="ltap_fcaml"
        class="form-control form-control-sm"
        formControlName="ltap_fcaml"
        (change)="onFcamlChange($event)"
        min="0"
      />
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <!-- FIELD 5: Length of Metal Loss in Circumferential Direction (c) - DEPRECATED  -->
  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <!-- COMMENTED OUT: No longer needed as an input field
  <div class="form-group row align-items-center">
    <div class="col-sm-8 align-self-center">
      <label for="ltap_circLength_c">
        Length of the metal loss in the circ. direction, c&nbsp;
        <span *ngIf="intUnit === 0">(inch)</span>
        <span *ngIf="intUnit === 1">(mm)</span>:
      </label>
    </div>
    <div class="col-sm-4">
      <input
        appRestrictInput
        type="number"
        id="ltap_circLength_c"
        name="ltap_circLength_c"
        class="form-control form-control-sm"
        formControlName="ltap_circLength_c"
        (change)="onCChange($event)"
        min="0"
      />
    </div>
  </div>
  -->

  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <!-- FIELD 6: H_ml - Axial Distance to Shell-to-Bottom Joint (TANK SPECIFIC)    -->
  <!-- This is used for hydrostatic pressure calculations in MFH mode              -->
  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <div class="form-group row align-items-center">
    <div class="col-sm-8 align-self-center">
      <label for="ltap_hml">
        Axial distance from the lower edge of the metal loss area to the bottom of the tank, H_ml&nbsp;
        <span *ngIf="intUnit === 0">(ft)</span>
        <span *ngIf="intUnit === 1">(m)</span>:
      </label>
    </div>
    <div class="col-sm-4">
      <input
        appRestrictInput
        type="text"
        id="ltap_hml"
        name="ltap_hml"
        class="form-control form-control-sm"
        formControlName="ltap_hml"
        (change)="onHmlChange($event)"
        autocomplete="off"
      />
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <!-- FIELD 7: Lmsd - Distance to Nearest Structural Discontinuity                -->
  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <div class="form-group row align-items-center">
    <div class="col-sm-8 align-self-center">
      <label for="ltap_lmsd">
        Distance from the edge of the metal loss to the nearest major structural discontinuity, Lmsd&nbsp;
        <span *ngIf="intUnit === 0">(inch)</span>
        <span *ngIf="intUnit === 1">(mm)</span>:
      </label>
    </div>
    <div class="col-sm-4">
      <input
        appRestrictInput
        type="number"
        id="ltap_lmsd"
        name="ltap_lmsd"
        class="form-control form-control-sm"
        formControlName="ltap_lmsd"
        (change)="onLmsdChange($event)"
        min="0"
      />
    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <!-- FIELD 8: gr - Groove-Like Flaw Radius (optional, enabled only for Groove)   -->
  <!-- ═══════════════════════════════════════════════════════════════════════════ -->
  <div class="form-group row align-items-center">
    <div class="col-sm-8 align-self-center">
      <label for="ltap_grooveRadius_gr">
        Radius at the base of the groove-like flaw, gr&nbsp;
        <span *ngIf="intUnit === 0">(inch)</span>
        <span *ngIf="intUnit === 1">(mm)</span>:
      </label>
    </div>
    <div class="col-sm-4">
      <input
        appRestrictInput
        type="number"
        id="ltap_grooveRadius_gr"
        name="ltap_grooveRadius_gr"
        class="form-control form-control-sm"
        formControlName="ltap_grooveRadius_gr"
        (change)="onGrChange($event)"
        min="0"
      />
    </div>
  </div>

</ng-container>